import { useDataProvider } from "@refinedev/core";

import type {
  CostDailyRow,
  CuratorActivityRow,
  ErrorBucketsRow,
  EventsDailyRow,
  FixedCostDailyRow,
  HitlBacklogRow,
  HitlDominationRow,
  OverviewRow,
  ScheduledCoverageRow,
  SiteQualityRow,
  StagePerfDailyRow,
  TodayCoverageRow,
} from "@/types/tina";

/**
 * Which of the three mutually exclusive spend buckets a metrics read wants —
 * the `job_class` query param on `/insights/cost` and `/insights/events`.
 *
 * **The server default is `"scrape"`, not `"all"`.** Omitting the param means
 * every enrichment and embedding row is filtered out before IMS sees it, which
 * is why enrichment spend was once invisible on `/tina/home`. A caller
 * that classifies client-side (the spend chart, via `spendClassForCost`) must
 * pass `"all"` explicitly.
 *
 * Twin of tina `src/db/queries/metrics.py::JobClass`. `/insights/overview` does
 * **not** accept it — see the KPI-strip note in `pages/tina/home/index.tsx`.
 */
export type JobClass = "scrape" | "enrichment" | "all";

export const useMetricsRepository = () => {
  const { custom } = useDataProvider()("tina");

  const get = async <T,>(
    urlPath: string,
    query?: Record<string, unknown>,
  ): Promise<T> => {
    const response = await custom!({
      url: "",
      method: "get",
      query,
      meta: { urlPath },
    });
    return response.data as T;
  };

  return {
    getOverview: (since: string, until: string) =>
      get<OverviewRow>("insights/overview", { since, until }),
    getStages: (since: string, until: string, stage?: string) =>
      get<StagePerfDailyRow[]>("insights/stages", { since, until, stage }),
    getCost: (
      since: string,
      until: string,
      stage?: string,
      job_class?: JobClass,
    ) =>
      get<CostDailyRow[]>("insights/cost", { since, until, stage, job_class }),
    /**
     * Cloud Run + subscription spend per day, item and category. Same
     * `since`/`until` convention as `getCost`: UTC days, both ends inclusive.
     */
    getFixedCost: (since: string, until: string) =>
      get<FixedCostDailyRow[]>("insights/fixed-cost", { since, until }),
    getEvents: (since: string, until: string, job_class?: JobClass) =>
      get<EventsDailyRow[]>("insights/events", { since, until, job_class }),
    getCuratorActivity: () =>
      get<CuratorActivityRow[]>("insights/curator-activity"),
    /** `content_type` narrows to one class of source. Omit it for every real site. */
    getSites: (content_type?: string) =>
      get<SiteQualityRow[]>("insights/sites", { content_type }),
    getErrors: (since: string, until: string, stage?: string) =>
      get<ErrorBucketsRow[]>("insights/errors", { since, until, stage }),
    getHitlBacklog: () => get<HitlBacklogRow[]>("insights/hitl-backlog"),
    getHitlDomination: () =>
      get<HitlDominationRow[]>("insights/hitl-domination"),
    getTodayCoverage: (since: string) =>
      get<TodayCoverageRow[]>("insights/today-coverage", { since }),
    getScheduledCoverage: () =>
      get<ScheduledCoverageRow[]>("insights/scheduled-coverage"),
  };
};
